import { useParams } from 'react-router';

import {
  getRole,
  RegisteredRole,
  RoleRegistryKey,
  roleRegistryKeys,
} from '@components/features/Restricted/registry';
import { UserMatchPartial } from '@components/features/Restricted/utils';
import { useUserQuery } from '@queries/user';

import { User } from '@/types/users';

/**
 * Gets the `roleId` for a user in a given team.
 */
function getTeamRoleId(user: User, teamUuid?: string): number | undefined {
  if (!teamUuid) return undefined;
  return user.teams.find((t) => t.uuid === teamUuid)?.roleId;
}

/**
 * Checks if the role matches the given user partial.
 */
export function isRoleMatch(
  roleKey: RoleRegistryKey,
  user: UserMatchPartial
): boolean {
  const role = getRole(roleKey);

  if (role.type === 'group') {
    return role.roles.some((r) => isRoleMatch(r.key, user));
  }

  let match = true;
  if ('roleId' in role.match && role.match.roleId !== undefined) {
    match &&= role.match.roleId === user.roleId;
  }
  if ('roleName' in role.match && role.match.roleName !== undefined) {
    match &&= role.match.roleName === user.roleName;
  }
  return match;
}

export interface MatchRolesOptions {
  context?: 'global' | 'team';
}

/**
 * Find the roles that the user matches.
 */
export function matchRoles(
  user: UserMatchPartial,
  options?: MatchRolesOptions
): Readonly<RegisteredRole>[] {
  return roleRegistryKeys.flatMap((key) => {
    // Filter out group roles
    const role = getRole(key);
    if (role.type === 'group') return [];

    // Filter by context
    if (options?.context && role.context !== options.context) return [];

    // Filter by user match
    const matched = isRoleMatch(key, user);
    if (!matched) return [];

    return role;
  });
}

export interface UseAllowOptions {
  /**
   * An optional team UUID to check the user's role against.
   * @default `useParams().teamUuid`
   */
  teamUuid?: string;
}

export interface UseAllowResult {
  /**
   * Whether the user data is still loading.
   */
  isPending: boolean;
  /**
   * Whether the user has access based on the given role requirement(s).
   */
  enabled: boolean;
}

/**
 * Checks if the current user has access based on the given role(s).
 *
 * @note If the user is a superuser, they have access to everything
 */
export function useAllow(
  allow: RoleRegistryKey | RoleRegistryKey[],
  options?: UseAllowOptions
): UseAllowResult {
  const params = useParams<{ teamUuid?: string }>();
  const { data, isLoading } = useUserQuery();

  if (!data) {
    return { isPending: isLoading, enabled: false };
  }

  const teamUuid = options?.teamUuid ?? params.teamUuid;
  const user: UserMatchPartial = {
    roleName: data.roleName,
    roleId: getTeamRoleId(data, teamUuid),
  };

  if (isRoleMatch('superuser', user)) {
    return { isPending: false, enabled: true };
  } else {
    const roleKeys = Array.isArray(allow) ? allow : [allow];
    const enabled = roleKeys.some((key) => isRoleMatch(key, user));
    return { isPending: false, enabled };
  }
}
